import { LinhaOrcamento, ResultadoOrcamento } from "@/services/orcamentoPublico";
import { formatarMoeda, formatarPercentual } from "./formato";

type Props = {
  resultado: ResultadoOrcamento | null;
  carregando: boolean;
  onAlternar: (codigo: string) => void;
};

/**
 * Tabela de específicas e serviços. Todos os valores vêm calculados da API —
 * nenhum campo é editável e não existe desconto manual: no site o desconto é
 * só o automático do cadastro da turma.
 */
export function CatalogoDisciplinas({ resultado, carregando, onAlternar }: Props) {
  if (!resultado) {
    return (
      <div className="op-panel" style={{ padding: "44px 20px", textAlign: "center", color: "var(--op-faint)" }}>
        {carregando ? "Carregando as específicas..." : "Escolha uma turma para ver as específicas."}
      </div>
    );
  }

  return (
    <div className="op-panel">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          borderBottom: "1px solid var(--op-line)",
          background: "var(--op-surface-2)",
          borderRadius: "14px 14px 0 0",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 15 }}>{resultado.turmaNome}</span>
        <MensagemDesconto resultado={resultado} />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="op-cat">
          <thead>
            <tr>
              <th scope="col">Específica / serviço</th>
              <th scope="col">Valor</th>
              <th scope="col">Desconto</th>
              <th scope="col">Você paga</th>
            </tr>
          </thead>
          <tbody>
            {resultado.linhas.map((linha) => (
              <Linha key={linha.codigo} linha={linha} onAlternar={onAlternar} />
            ))}
          </tbody>
          {resultado.quantidadeEspecificas > 0 || resultado.totalFinal > 0 ? (
            <tfoot>
              <tr>
                <td style={{ fontWeight: 600, color: "var(--op-muted)" }}>Matrícula</td>
                <td className="op-num">{formatarMoeda(resultado.matriculaValor)}</td>
                <td className="op-num">—</td>
                <td className="op-num" style={{ fontWeight: 700 }}>
                  {formatarMoeda(resultado.matriculaValor)}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 800, fontSize: 17 }}>Total</td>
                <td className="op-num" style={{ color: "var(--op-faint)", textDecoration: "line-through" }}>
                  {formatarMoeda(resultado.totalBruto)}
                </td>
                <td className="op-num" style={{ color: "var(--op-crimson)", fontWeight: 700 }}>
                  {resultado.totalDesconto > 0 ? `−${formatarMoeda(resultado.totalDesconto)}` : "—"}
                </td>
                <td
                  className="op-num"
                  style={{ fontWeight: 800, fontSize: 19, color: "var(--op-indigo)" }}
                >
                  {formatarMoeda(resultado.totalFinal)}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}

function MensagemDesconto({ resultado }: { resultado: ResultadoOrcamento }) {
  if (resultado.quantidadeEspecificas === 0) {
    return (
      <span style={{ fontSize: 13, color: "var(--op-muted)" }}>
        Selecione as matérias para ver o desconto
      </span>
    );
  }

  // O cadastro só tem faixas de 1 a 6 específicas: acima disso o desconto zera.
  // Avisar é melhor do que deixar o total subir sem explicação.
  if (resultado.semFaixaCadastrada) {
    return (
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--op-amber-ink)",
          background: "var(--op-amber-wash)",
          padding: "4px 9px",
          borderRadius: 6,
        }}
      >
        Não há faixa de desconto cadastrada para {resultado.quantidadeEspecificas} específicas.
        Fale com a secretaria.
      </span>
    );
  }

  return (
    <span style={{ fontSize: 13, color: "var(--op-muted)" }}>
      Com <b style={{ color: "var(--op-crimson)" }}>{resultado.quantidadeEspecificas}</b>{" "}
      {resultado.quantidadeEspecificas === 1 ? "específica" : "específicas"} seu desconto de turma é{" "}
      <b style={{ color: "var(--op-crimson)" }}>{formatarPercentual(resultado.percDescontoFaixa)}</b>
    </span>
  );
}

function Linha({ linha, onAlternar }: { linha: LinhaOrcamento; onAlternar: (codigo: string) => void }) {
  const alternar = () => {
    if (!linha.bloqueado) onAlternar(linha.codigo);
  };

  return (
    <tr
      className="op-item"
      data-marcado={linha.selecionado}
      data-bloqueado={linha.bloqueado}
      onClick={alternar}
      onKeyDown={(evento) => {
        if (evento.key === "Enter" || evento.key === " ") {
          evento.preventDefault();
          alternar();
        }
      }}
    >
      <td>
        <span style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <input
            type="checkbox"
            checked={linha.selecionado}
            disabled={linha.bloqueado}
            onChange={alternar}
            onClick={(evento) => evento.stopPropagation()}
            aria-label={linha.nome}
            style={{ width: 17, height: 17, flex: "none", accentColor: "var(--op-indigo)" }}
          />
          <span>
            <span style={{ fontWeight: 600 }}>{linha.nome}</span>
            {linha.selecionadoAutomaticamente && (
              <span
                style={{
                  marginLeft: 7,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  color: "var(--op-teal)",
                  background: "rgba(42,175,192,.14)",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                incluída
              </span>
            )}
            <Legenda linha={linha} />
          </span>
        </span>
      </td>
      <td className="op-num">
        {linha.selecionado && linha.valorDesconto > 0 ? (
          <span style={{ color: "var(--op-faint)", textDecoration: "line-through" }}>
            {formatarMoeda(linha.valor)}
          </span>
        ) : (
          formatarMoeda(linha.valor)
        )}
      </td>
      <td className="op-num">
        {linha.selecionado && linha.valorDesconto > 0 ? (
          <span style={{ color: "var(--op-crimson)", fontWeight: 700 }}>
            −{formatarPercentual(linha.percDesconto)}
          </span>
        ) : (
          <span style={{ color: "var(--op-faint)" }}>—</span>
        )}
      </td>
      <td className="op-num" style={{ fontWeight: linha.selecionado ? 700 : 400 }}>
        {linha.selecionado ? formatarMoeda(linha.valorFinal) : "—"}
      </td>
    </tr>
  );
}

function Legenda({ linha }: { linha: LinhaOrcamento }) {
  const texto = legendaDe(linha);
  if (!texto) return null;

  return (
    <>
      <br />
      <span style={{ fontSize: 12, color: "var(--op-faint)" }}>{texto}</span>
    </>
  );
}

function legendaDe(linha: LinhaOrcamento): string | null {
  // O único bloqueio que resta é o da específica travada por outra já escolhida,
  // e ele não leva texto: o motivo fica evidente pela linha recém-marcada.
  if (linha.bloqueado) return null;
  if (linha.tipo === "MONITORIA") return "acompanha a específica vinculada";
  return null;
}

export default CatalogoDisciplinas;
